import { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InventoryIcon from '@mui/icons-material/Inventory2';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { createEmptyFilters } from '../activity/types';
import { ProductSelectionMode, UNLIMITED_PRODUCT_SELECTION, type ProductSelectionContext } from '../product';
import { StockSelectionMode } from '../stock';
import { stockAuditService } from '../../services/stockAuditService';
import { extractErrorMessage } from '../../utils/errorUtils';
import type { Product } from '../../types/product';
import type { Stock } from '../../types/stock';
import type { StockAudit } from '../../types/stockAudit';

interface AuditDraft {
  stock: Stock | null;
  products: Product[];
}

const getDraftKey = (companyId: number): string => `stockAuditDraft:${companyId}`;

const loadDraft = (companyId: number): AuditDraft => {
  try {
    const saved = sessionStorage.getItem(getDraftKey(companyId));
    if (saved) {
      return JSON.parse(saved) as AuditDraft;
    }
  } catch (err) {
    console.error('Failed to restore stock audit draft:', err);
  }
  return { stock: null, products: [] };
};

export default function CreateStockAuditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  if (!id) {
    throw new Error('Company ID is required');
  }

  const companyId = parseInt(id);
  const auditsPath = `/companies/${companyId}/stock-audits`;

  const [draft, setDraft] = useState<AuditDraft>(() => loadDraft(companyId));
  const [audits, setAudits] = useState<StockAudit[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    sessionStorage.setItem(getDraftKey(companyId), JSON.stringify(draft));
  }, [companyId, draft]);

  // Handle returns from the stock / product selection pages
  useEffect(() => {
    const selectedStock = location.state?.selectedStock as Stock | undefined;
    const selectedProducts = location.state?.activityFilters?.products as Product[] | undefined;

    if (selectedStock) {
      setDraft((prev) => ({ ...prev, stock: selectedStock }));
    }
    if (selectedProducts) {
      setDraft((prev) => ({ ...prev, products: selectedProducts }));
    }
    if (selectedStock || selectedProducts) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;
    stockAuditService.list(companyId)
      .then((data) => {
        if (!cancelled) {
          setAudits(data);
        }
      })
      .catch((err) => {
        console.error('Failed to load stock audits:', err);
        if (!cancelled) {
          setError(extractErrorMessage(err, 'Не вдалося завантажити список ревізій. Оновіть сторінку.'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const openAudit = (auditId: number) => {
    sessionStorage.removeItem(getDraftKey(companyId));
    navigate(`${auditsPath}/${auditId}`, { replace: true });
  };

  const findOpenAudit = (list: StockAudit[], stockId: number): StockAudit | undefined =>
    list.find((audit) => audit.stockId === stockId && audit.status === 'IN_PROGRESS');

  const handleOpenStockSelector = () => {
    navigate(`/companies/${companyId}/stocks`, {
      state: {
        mode: StockSelectionMode.SINGLE_STOCK_SELECTION,
        currentStockId: draft.stock?.id,
        returnPath: location.pathname,
      },
    });
  };

  const handleOpenProductSelector = () => {
    const context: ProductSelectionContext = {
      mode: ProductSelectionMode.MULTIPLE_PRODUCT_SELECTION,
      returnPath: location.pathname,
      activityFilters: { ...createEmptyFilters(), products: draft.products },
      maxSelection: UNLIMITED_PRODUCT_SELECTION,
      infoText: 'Виберіть продукти для ревізії. Без вибору перевіряється весь склад.',
    };
    navigate(`/companies/${companyId}/products`, { state: context });
  };

  const openAuditOnSelectedStock = audits && draft.stock ? findOpenAudit(audits, draft.stock.id) : undefined;

  const handleSubmit = async () => {
    if (!draft.stock) {
      return;
    }
    const stockId = draft.stock.id;

    if (openAuditOnSelectedStock) {
      openAudit(openAuditOnSelectedStock.id);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const created = await stockAuditService.create({
        companyId,
        stockId,
        productIds: draft.products.map((product) => product.id),
      });
      openAudit(created.id);
    } catch (err) {
      // Someone opened an audit on this stock after our list was loaded: join it
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const fresh = await stockAuditService.list(companyId);
        const concurrent = findOpenAudit(fresh, stockId);
        if (concurrent) {
          openAudit(concurrent.id);
          return;
        }
      }
      console.error('Error creating stock audit:', err);
      setError(extractErrorMessage(err, 'Не вдалося створити ревізію. Спробуйте ще раз.'));
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            sessionStorage.removeItem(getDraftKey(companyId));
            navigate(auditsPath);
          }}
          size="small"
        >
          Назад
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Нова ревізія
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          mt: 2,
        }}
      >
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            Склад *
          </Typography>
          {draft.stock ? (
            <Chip
              icon={<WarehouseIcon />}
              label={draft.stock.name}
              onClick={handleOpenStockSelector}
              onDelete={() => setDraft((prev) => ({ ...prev, stock: null }))}
              color="primary"
              variant="outlined"
              sx={{ width: '100%', justifyContent: 'space-between', height: 40 }}
            />
          ) : (
            <Button
              variant="outlined"
              color="error"
              startIcon={<WarehouseIcon />}
              onClick={handleOpenStockSelector}
              fullWidth
              sx={{ justifyContent: 'flex-start' }}
            >
              Вибрати склад
            </Button>
          )}
          {openAuditOnSelectedStock && (
            <Typography
              variant="caption"
              color="text.secondary"
            >
              На цьому складі вже є відкрита ревізія — буде відкрито її
            </Typography>
          )}
        </Box>

        {!openAuditOnSelectedStock && (
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Продукти (порожньо — весь склад)
            </Typography>
            {draft.products.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {draft.products.map((product) => (
                  <Chip
                    key={product.id}
                    icon={<InventoryIcon />}
                    label={product.name}
                    onDelete={() =>
                      setDraft((prev) => ({
                        ...prev,
                        products: prev.products.filter((p) => p.id !== product.id),
                      }))
                    }
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
            <Button
              variant="outlined"
              startIcon={<InventoryIcon />}
              onClick={handleOpenProductSelector}
              fullWidth
              sx={{ justifyContent: 'flex-start' }}
            >
              {draft.products.length > 0 ? 'Змінити продукти' : 'Вибрати продукти'}
            </Button>
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!draft.stock || audits === null || submitting}
          fullWidth
        >
          {submitting ? <CircularProgress size={24} /> : openAuditOnSelectedStock ? 'Відкрити' : 'Почати'}
        </Button>
      </Box>
    </Box>
  );
}
